import React, { useEffect, useRef, useState, useCallback } from "react";
import { X, ZoomIn, ZoomOut, Check, Loader2 } from "lucide-react";

/* Position a photo inside a circle before it's saved.

   The server used to choose the crop on its own ("the most interesting
   region"), which is a guess — and on a full-length photo it framed a
   shirt instead of a face. Now the person drags the photo into place and
   zooms, and the server receives an already-square image it only resizes.

   Hand-built rather than a cropping library (CLAUDE.md: no new dependency
   without cause): it is one transform, a clamp and a canvas draw. Pointer
   events cover mouse, touch and pen with one code path, and a second
   pointer turns the gesture into a pinch.

   The image always covers the whole circle — it can't be dragged so far,
   or zoomed out so much, that an empty edge shows. */
const OUTPUT = 512;       // exported square; the server scales it to 256
const MAX_ZOOM = 4;

/**
 * Turn a chosen file into an image the page is allowed to display.
 *
 * NOT URL.createObjectURL: that yields a `blob:` URL, and the live
 * Content-Security-Policy (ops/nginx/*.conf) allows images from 'self',
 * data: and https: only. The browser silently refused the blob, the image
 * never loaded, and the cropper spun forever — on every live property, and
 * never in testing, where no CSP was sent.
 *
 * Decoded straight from the File instead (createImageBitmap takes no URL,
 * so no CSP rule applies), scaled to at most 2048px — plenty for a 512px
 * crop, and far lighter than a 12-megapixel original — and handed over as
 * a data: URL, which the policy allows. Fixes it with a deploy, no nginx
 * change on any server. Falls back to reading the file as a data: URL
 * where createImageBitmap is missing.
 */
export async function preparePhoto(file, maxSide = 2048) {
  let bitmap = null;
  if (typeof createImageBitmap === "function") {
    try { bitmap = await createImageBitmap(file, { imageOrientation: "from-image" }); }
    catch { try { bitmap = await createImageBitmap(file); } catch { bitmap = null; } }
  }
  if (bitmap) {
    const k = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * k));
    canvas.height = Math.max(1, Math.round(bitmap.height * k));
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";                       // a transparent PNG must not turn black as JPEG
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();
    return canvas.toDataURL("image/jpeg", 0.92);
  }
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error || new Error("Couldn't read that file"));
    r.readAsDataURL(file);
  });
}

export default function PhotoCropper({ src, onCancel, onSave, busy }) {
  const stageRef = useRef(null);
  const [stage, setStage] = useState(0);            // circle diameter, CSS px
  const [nat, setNat] = useState(null);             // natural image size
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });   // image top-left within the stage
  // A photo that can't be decoded must say so, not spin forever.
  const [loadError, setLoadError] = useState(false);
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const imgRef = useRef(null);

  // Measure the stage; it is fluid so it fits a 360px phone.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => setStage(Math.floor(el.getBoundingClientRect().width));
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // "cover" scale at zoom 1: the shorter side exactly spans the circle.
  const base = nat && stage ? stage / Math.min(nat.w, nat.h) : 1;
  const scale = base * zoom;
  const dispW = nat ? nat.w * scale : 0;
  const dispH = nat ? nat.h * scale : 0;

  const clamp = useCallback((p, w = dispW, h = dispH) => ({
    x: Math.min(0, Math.max(stage - w, p.x)),
    y: Math.min(0, Math.max(stage - h, p.y)),
  }), [stage, dispW, dispH]);

  // Start centred once both sizes are known.
  useEffect(() => {
    if (!nat || !stage) return;
    const w = nat.w * base, h = nat.h * base;
    setZoom(1);
    setPos({ x: (stage - w) / 2, y: (stage - h) / 2 });
  }, [nat, stage]);

  /** Zoom about a point in stage coordinates, keeping that point still. */
  const zoomTo = useCallback((next, cx = stage / 2, cy = stage / 2) => {
    if (!nat) return;
    const z = Math.min(MAX_ZOOM, Math.max(1, next));
    const newScale = base * z;
    const ix = (cx - pos.x) / scale, iy = (cy - pos.y) / scale;     // image px under the point
    const p = { x: cx - ix * newScale, y: cy - iy * newScale };
    setZoom(z);
    setPos(clamp(p, nat.w * newScale, nat.h * newScale));
  }, [nat, base, scale, pos, stage, clamp]);

  function local(e) {
    const r = stageRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  function onPointerDown(e) {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, local(e));
    const pts = [...pointers.current.values()];
    gesture.current = pts.length === 1
      ? { type: "drag", start: pts[0], from: pos }
      : { type: "pinch", dist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y), zoom };
  }

  function onPointerMove(e) {
    if (!pointers.current.has(e.pointerId) || !gesture.current) return;
    pointers.current.set(e.pointerId, local(e));
    const pts = [...pointers.current.values()];
    const g = gesture.current;
    if (g.type === "drag" && pts.length === 1) {
      setPos(clamp({ x: g.from.x + pts[0].x - g.start.x, y: g.from.y + pts[0].y - g.start.y }));
    } else if (g.type === "pinch" && pts.length >= 2) {
      const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      zoomTo(g.zoom * (d / g.dist), (pts[0].x + pts[1].x) / 2, (pts[0].y + pts[1].y) / 2);
    }
  }

  function onPointerUp(e) {
    pointers.current.delete(e.pointerId);
    const pts = [...pointers.current.values()];
    // Lifting one finger of a pinch continues as a drag from where it is.
    gesture.current = pts.length === 1 ? { type: "drag", start: pts[0], from: pos } : null;
  }

  function onWheel(e) {
    e.preventDefault();
    const p = local(e);
    zoomTo(zoom * (e.deltaY < 0 ? 1.08 : 1 / 1.08), p.x, p.y);
  }

  // A wheel listener must be non-passive to stop the page scrolling.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  });

  function onKeyDown(e) {
    const step = e.shiftKey ? 20 : 6;
    const moves = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
    if (moves[e.key]) { e.preventDefault(); setPos(clamp({ x: pos.x + moves[e.key][0], y: pos.y + moves[e.key][1] })); }
    if (e.key === "+" || e.key === "=") { e.preventDefault(); zoomTo(zoom * 1.1); }
    if (e.key === "-") { e.preventDefault(); zoomTo(zoom / 1.1); }
  }

  async function save() {
    if (!nat) return;
    // The visible square, mapped back to the photo's own pixels.
    const sx = -pos.x / scale, sy = -pos.y / scale, size = stage / scale;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = OUTPUT;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, OUTPUT, OUTPUT);
    ctx.drawImage(imgRef.current, sx, sy, size, size, 0, 0, OUTPUT, OUTPUT);
    // Re-encoding here also means no EXIF leaves the device at all; the
    // server strips it again regardless.
    canvas.toBlob(blob => blob && onSave(new File([blob], "avatar.jpg", { type: "image/jpeg" })), "image/jpeg", 0.92);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm sm:p-4"
      role="dialog" aria-modal="true" aria-label="Adjust photo">
      <div className="bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-5 pt-4 pb-1">
          <h3 className="font-semibold text-gray-900">Adjust photo</h3>
          <button onClick={onCancel} aria-label="Cancel" disabled={busy}
            className="w-11 h-11 -mr-2 flex items-center justify-center rounded-xl hover:bg-gray-100">
            <X size={18} className="text-gray-400" />
          </button>
        </div>
        <p className="px-5 text-xs text-gray-500">Drag to position{typeof window !== "undefined" && "ontouchstart" in window ? ", pinch to zoom" : ", scroll or use the slider to zoom"}.</p>

        <div className="px-5 pt-4">
          <div ref={stageRef} tabIndex={0} onKeyDown={onKeyDown}
            onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
            className="relative mx-auto w-full max-w-[300px] aspect-square overflow-hidden rounded-2xl bg-gray-900
              cursor-grab active:cursor-grabbing select-none focus:outline-none focus:ring-2 focus:ring-amber-400"
            style={{ touchAction: "none" }}
            aria-label="Photo position. Arrow keys move it, plus and minus zoom.">
            <img ref={imgRef} src={src} alt="" draggable={false}
              onLoad={e => setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
              onError={() => setLoadError(true)}
              style={{ position: "absolute", left: 0, top: 0, width: dispW || "auto", height: dispH || "auto",
                transform: `translate(${pos.x}px, ${pos.y}px)`, maxWidth: "none", visibility: nat ? "visible" : "hidden" }} />
            {/* The circle: everything outside it dimmed, so what you see is exactly what's saved. */}
            <div className="pointer-events-none absolute inset-0 rounded-full"
              style={{ boxShadow: "0 0 0 9999px rgba(17,24,39,0.55)", outline: "2px solid rgba(255,255,255,0.85)", outlineOffset: "-2px" }} />
            {!nat && !loadError && <Loader2 className="absolute inset-0 m-auto animate-spin text-white/70" size={22} />}
            {loadError && (
              <p role="alert" className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-white/90">
                Couldn't open this photo. Try a JPG or PNG.
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 max-w-[300px] mx-auto mt-3">
            <button type="button" onClick={() => zoomTo(zoom / 1.2)} aria-label="Zoom out"
              className="w-11 h-11 shrink-0 flex items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100">
              <ZoomOut size={17} />
            </button>
            <input type="range" min={1} max={MAX_ZOOM} step={0.01} value={zoom} aria-label="Zoom"
              onChange={e => zoomTo(Number(e.target.value))}
              className="flex-1 h-11 accent-amber-500 cursor-pointer" />
            <button type="button" onClick={() => zoomTo(zoom * 1.2)} aria-label="Zoom in"
              className="w-11 h-11 shrink-0 flex items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100">
              <ZoomIn size={17} />
            </button>
          </div>
        </div>

        <div className="flex gap-2 px-5 pt-3 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
          <button type="button" onClick={onCancel} disabled={busy}
            className="flex-1 min-h-[48px] rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={save} disabled={!nat || busy}
            className="flex-1 min-h-[48px] inline-flex items-center justify-center gap-1.5 rounded-xl text-sm font-semibold text-white
              bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:opacity-50">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Save photo
          </button>
        </div>
      </div>
    </div>
  );
}
